/**
 * VariableCard component for editing linked table variables
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { Button, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@loader/ui';
import { VARIABLE_NAME_MAX_LENGTH } from '@loader/shared-types';

import type { LinkedTableVariable } from '@/lib/types';

const DEBOUNCE_DELAY = 300; // ms — matches FilterEditor

/**
 * Sanitize variable name to valid identifier (alphanumeric + underscore)
 */
function sanitizeVariableName(input: string): string {
  return input
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .replace(/^[0-9]/, '_$&')
    .slice(0, VARIABLE_NAME_MAX_LENGTH);
}

export interface VariableCardProps {
  variable: LinkedTableVariable;
  canEdit: boolean;
  onUpdate?: (variable: LinkedTableVariable) => void;
  onRemove?: (variableId: string) => void;
}

export function VariableCard({ variable, canEdit, onUpdate, onRemove }: VariableCardProps) {
  // Local state only for default_value (debounced — affects preview query)
  // Name updates go directly upstream (they don't trigger preview refetch)
  const [localDefaultValue, setLocalDefaultValue] = useState(variable.default_value);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Sync local default_value when upstream changes (e.g. type change resets it)
  useEffect(() => {
    setLocalDefaultValue(variable.default_value);
  }, [variable.default_value]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const debouncedUpdate = useCallback(
    (updated: LinkedTableVariable) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        onUpdate?.(updated);
      }, DEBOUNCE_DELAY);
    },
    [onUpdate]
  );

  // Name: update immediately (doesn't trigger preview refetch)
  const handleNameChange = (value: string) => {
    onUpdate?.({ ...variable, name: sanitizeVariableName(value) });
  };

  // Default value: debounce (triggers preview refetch)
  const handleDefaultValueChange = (value: string) => {
    setLocalDefaultValue(value);
    debouncedUpdate({ ...variable, default_value: value });
  };

  // Type: update immediately (discrete selection, not typing)
  const handleTypeChange = (value: string) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setLocalDefaultValue('');
    onUpdate?.({
      ...variable,
      type: value as LinkedTableVariable['type'],
      default_value: '',
    });
  };

  return (
    <div className="relative space-y-2 rounded-md border border-border bg-background p-2">
      {/* Remove button */}
      {canEdit && (
        <Button
          variant="outline"
          size="icon"
          className="absolute -top-2 -right-2 h-5 w-5 rounded-full"
          onClick={() => onRemove?.(variable.id)}
        >
          <X className="h-2.5 w-2.5" />
        </Button>
      )}

      {/* Row 1: Name + Type side by side */}
      <div className="flex gap-1.5">
        <div className="min-w-0 flex-1 space-y-0.5">
          <Label className="text-muted-foreground text-xs">Name</Label>
          <div className="relative">
            <span className="text-muted-foreground pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm">$</span>
            <Input
              value={variable.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="start_date"
              className="bg-background pl-5 text-sm"
              disabled={!canEdit}
            />
          </div>
        </div>
        <div className="w-20 shrink-0 space-y-0.5">
          <Label className="text-muted-foreground text-xs">Type</Label>
          <Select
            value={variable.type}
            onValueChange={handleTypeChange}
            disabled={!canEdit}
          >
            <SelectTrigger className="w-full text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="number">Number</SelectItem>
              <SelectItem value="date">Date</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Row 2: Default value */}
      <div>
        <Input
          type={variable.type === 'number' ? 'number' : variable.type === 'date' ? 'date' : 'text'}
          value={localDefaultValue}
          onChange={(e) => handleDefaultValueChange(e.target.value)}
          placeholder={
            variable.type === 'date'
              ? 'YYYY-MM-DD'
              : variable.type === 'number'
                ? '0'
                : 'value'
          }
          className="bg-background text-sm"
          disabled={!canEdit}
        />
      </div>
    </div>
  );
}
